import { formatCurrencyNoDecimals, formatPercentage } from '@if/core/utils'
import { FormSubmitButton, StepLinearProgress, StepLinearProgressStep, useFormContainerContext } from '@if/mui'
import { Card, CardActions, CardContent, CardHeader, Divider, Stack, Typography } from '@mui/material'
import {
  APPROVAL_LIKELIHOOD_TIERS,
  ApprovalLikelihood,
  CONVERSION_MODEL,
  conversionProbability,
  getApprovalLikelihood,
} from '@src/data/approvalLikelihood'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { CustomBuydownFormValues } from '../customBuydown-schema'

// Paired across MUI's standard semantic palette (worst→best); no custom colors introduced.
const COLORS: Record<ApprovalLikelihood, 'error' | 'warning' | 'info' | 'success'> = {
  marginal: 'error',
  low: 'warning',
  moderate: 'warning',
  good: 'info',
  high: 'info',
  optimal: 'success',
  veryHigh: 'success',
}

const approvalSteps: StepLinearProgressStep[] = APPROVAL_LIKELIHOOD_TIERS.map(({ threshold, level }) => ({
  from: conversionProbability(threshold),
  color: COLORS[level],
}))

export interface PlanSummaryCardProps {
  adminFeeRate: number
  locked?: boolean
}

export const PlanSummaryCard = ({ adminFeeRate, locked = false }: PlanSummaryCardProps) => {
  const { t } = useTranslation()
  const { methods } = useFormContainerContext<CustomBuydownFormValues>()
  const contribution = useWatch({ control: methods.control, name: 'contribution' }) ?? 0
  const maxFinancingAmount = useWatch({ control: methods.control, name: 'maxFinancingAmount' })

  const planCode = `FLASH${Math.round(contribution * 10000)}/${Math.round(adminFeeRate * 100)}`
  const likelihood = getApprovalLikelihood(contribution)
  const probability = conversionProbability(contribution)

  return (
    <Card>
      <CardHeader title={t('customBuydown.summaryTitle')} />
      <CardContent>
        <Stack spacing={2} divider={<Divider />}>
          <Stack spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              {t('customBuydown.planCode')}
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              {planCode}
            </Typography>
          </Stack>
          <Stack spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              {t('customBuydown.yourFeeRate')}
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              {formatPercentage(contribution * 100)} %
            </Typography>
          </Stack>
          {maxFinancingAmount != null && maxFinancingAmount > 0 && (
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">
                {t('customBuydown.maxCost')}
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatCurrencyNoDecimals(contribution * maxFinancingAmount)}
              </Typography>
            </Stack>
          )}
          <Stack spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              {t('customBuydown.approvalLikelihood')}
            </Typography>
            <Typography variant="subtitle1" fontWeight="bold">
              {t(`customBuydown.approval.${likelihood}`)}
            </Typography>
            <StepLinearProgress
              steps={approvalSteps}
              value={probability}
              min={CONVERSION_MODEL.P_MIN}
              max={CONVERSION_MODEL.P_MAX}
              sx={{ height: 8, borderRadius: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              {t(`customBuydown.approvalHint.${likelihood}`)}
            </Typography>
          </Stack>
        </Stack>
      </CardContent>
      <CardActions>
        {locked ? (
          <Typography variant="body2" color="text.secondary" sx={{ width: '100%', textAlign: 'center', py: 1 }}>
            {t('customBuydown.inherited.setByBanner')}
          </Typography>
        ) : (
          <FormSubmitButton fullWidth label={t('customBuydown.saveAndApply')} />
        )}
      </CardActions>
    </Card>
  )
}
